import * as yup from 'yup';
import Button from '@ui/Button';
import Typography from '@ui/Typography';
import React, { useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import getSymbolFromCurrency from 'currency-symbol-map';
import { Controller, useForm } from 'react-hook-form';
import { fetchViaProxy } from '@/actions/proxy-fetch';
import Input from '../next-ui/Input/Input';
import Checkbox from '../next-ui/Checkbox';
import Tooltip from '@ui/Tooltip';
import { first, isEmpty, isNumber, trim } from 'lodash-es';
import { MdEdit, MdClose, MdCheck } from 'react-icons/md';
import PostImageSelector from './PostImageSelector/PostImageSelector';
import { authorizedServerFetch } from '@/actions/server.actions';
import { useBreakpointHooks } from '@/utils/break-points.provider';
import { useUpdateFundraiserDonation } from '@/actions/feed.actions';
import { getUserCurrency } from '@/utils/user.helper';
import { sanitizeNonProfitAddress } from '@/utils/nonProfitAddress.helper';
import AnimatedContent from './AnimatedContent';
import { IoMdInformationCircleOutline } from 'react-icons/io';
import { parseAddress } from '@/utils/stringHelpers';

const schemaPublic = yup.object().shape({
  firstName: yup
    .string()
    .required('First name is required')
    .min(2, 'First name must be at least 2 characters')
    .max(50, 'First name must be at most 50 characters'),
  lastName: yup
    .string()
    .required('Last name is required')
    .min(2, 'Last name must be at least 2 characters')
    .max(50, 'Last name must be at most 50 characters'),
  email: yup
    .string()
    .required('Email is required')
    .email('Email must be a valid email id'),
  agreeTerms: yup
    .boolean()
    .oneOf([true], 'Please agree to terms')
    .required('Please agree to terms'),
  // amount: yup
  //   .number()
  //   .required()
  //   .min(1, 'Minimum amount is 1')
  //   .typeError('Minimum amount is 1'),
});

const schemaCommon = yup.object().shape({
  agreeTerms: yup
    .boolean()
    .oneOf([true], 'Please agree to terms')
    .required('Please agree to terms'),
  amount: yup
    .number()
    .required()
    .min(1, 'Minimum amount is 1')
    .typeError('Minimum amount is 1'),
});

const PendingContributionScreen = (props) => {
  const {
    user,
    post,
    amount,
    fundraisingOrg,
    isLoggedIn,
    setShowSuccess,
    setLoggedOutUser,
    setTransactionState,
    handleFetchContributionIntent,
  } = props;

  const contributionUrl = post?.sourcePostData?.contributionUrl;

  const [noContributionUrlError, setNoContributionUrlError] = useState(false);

  const schema = isLoggedIn() ? schemaCommon : schemaPublic;

  const form = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      agreeTerms: false,
      amount: amount,
    },
    resolver: yupResolver(schema),
    mode: 'onChange',
  });

  const {
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = form;

  const amountVal = watch('amount');

  const handleFormSubmit = async (formData) => {
    if (!contributionUrl) {
      setNoContributionUrlError(true);
      return;
    }

    let payload = {
      fundraisingOrgId: fundraisingOrg.id,
      amount: formData.amount || 0,
      currency: isLoggedIn() ? getUserCurrency(user) : 'USD',
      postId: post?.id,
    };

    if (!isLoggedIn()) {
      payload = {
        ...payload,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
      };
      setLoggedOutUser({
        id: -1,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
      });
    }

    if (isLoggedIn()) {
      const { ok, data } = await fetchViaProxy(
        `/proxy/v2/protected/users/me/external/contributions/intent`,
        {
          method: 'POST',
          body: JSON.stringify(payload),
        },
      );

      console.log('PendingContributionScreen', data);

      if (ok) {
        const intentId = data?.data?._id.toString();
        handleOpenExternalLink(contributionUrl);
        setTransactionState('confirm');
        handleFetchContributionIntent(intentId, false);
      } else {
        // toast('Error in creating contribution intent', { type: 'error' });
      }
    } else {
      /* empty */
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_API_HOST}/v1/public/contributions/external/intent`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        },
      );

      console.log('raiseStripeContributionIntentPublic res', res);

      if (res.ok) {
        const { data } = await res.json();
        console.log('PendingContributionScreen', data);
        const intentId = data?._id.toString();
        handleOpenExternalLink(contributionUrl);
        // setTransactionState('confirm');
        // handleFetchContributionIntent(intentId, false);
        setTransactionState('pledged');
        setShowSuccess('user-external-contribution');
      } else {
        // toast('Error in creating contribution intent', { type: 'error' });
      }
    }
  };

  const handleOpenExternalLink = (url) => {
    let externalUrl = url;
    if (!/^https?:\/\//i.test(externalUrl)) {
      externalUrl = `https://${externalUrl}`;
    }
    window.open(externalUrl, '_blank');
  };

  return (
    <div className="flex flex-col">
      <div className="flex h-full flex-1 flex-col overflow-y-auto overflow-x-hidden">
        <div className="flex flex-1 flex-col justify-between gap-4">
          <div className="flex flex-col gap-4">
            <Typography variant="subtitle1">
              You are leaving Neki and being re-directed to the contribution
              page for {fundraisingOrg.name}. After completing your contribution
              on the external site, please save your proof of contribution.
            </Typography>

            <Typography>
              Donation Match and tax benefits are not available.
            </Typography>
          </div>

          <form
            className="flex flex-col justify-start gap-4 lg:flex-row lg:justify-between"
            onSubmit={handleSubmit(handleFormSubmit)}>
            <div className="flex flex-col gap-4 py-1">
              {!isLoggedIn() && (
                <>
                  <div className="flex flex-col gap-4 lg:flex-row">
                    <Controller
                      name="firstName"
                      control={control}
                      render={({ field: { ref, ...rest } }) => (
                        <Input
                          label="First Name"
                          required
                          {...rest}
                          error={errors.firstName?.message}
                        />
                      )}
                    />
                    <Controller
                      name="lastName"
                      control={control}
                      render={({ field: { ref, ...rest } }) => (
                        <Input
                          label="Last Name"
                          required
                          {...rest}
                          error={errors.lastName?.message}
                        />
                      )}
                    />
                  </div>
                  <Controller
                    name="email"
                    control={control}
                    render={({ field: { ref, ...rest } }) => (
                      <Input
                        label="Email Id"
                        required
                        {...rest}
                        error={errors.email?.message}
                      />
                    )}
                  />
                </>
              )}
              {isLoggedIn() && (
                <>
                  <Typography variant="body1" className="font-medium">
                    Contribution Amount <span className="text-red-700">*</span>
                  </Typography>
                  <Controller
                    name="amount"
                    control={control}
                    render={({ field: { ref, ...rest } }) => (
                      <Input
                        // label="Contribution Amount"
                        placeholder="Enter an amount"
                        required
                        {...rest}
                        adornment={
                          <Typography
                            variant="body1"
                            color="textSecondary"
                            className="!font-medium">
                            {/* {user?.location?.currencySymbol} */}
                            {isLoggedIn()
                              ? getSymbolFromCurrency(getUserCurrency(user))
                              : '$'}
                          </Typography>
                        }
                        error={errors.amount?.message}
                      />
                    )}
                  />
                </>
              )}
            </div>

            <div className="flex flex-col gap-4 self-end lg:gap-6">
              <Controller
                name="agreeTerms"
                control={control}
                render={({ field: { ref, ...rest } }) => (
                  <div className="flex flex-col items-start gap-1">
                    <div className="flex items-center justify-start gap-4">
                      <Checkbox
                        {...rest}
                        className="!ml-0 border-text-disabled font-extrabold"
                        style={{ marginLeft: 0, paddingLeft: 0 }}
                      />
                      <Typography
                        variant="body2"
                        color="textDisabled"
                        className="font-bold">
                        I agree to the{' '}
                        <Typography
                          variant="body2"
                          component="a"
                          color="hyperlink"
                          target="_blank"
                          href="https://www.neki.io/terms-and-conditions"
                          className="text-primary hover:underline">
                          Terms of Service
                        </Typography>{' '}
                        and{' '}
                        <Typography
                          variant="body2"
                          component="a"
                          color="hyperlink"
                          target="_blank"
                          href="https://www.neki.io/privacy-policy"
                          className="text-primary hover:underline">
                          Privacy Policy
                        </Typography>
                      </Typography>
                    </div>
                    <Typography
                      variant="body2"
                      color="error"
                      className={!errors.agreeTerms && 'hidden'}>
                      Please agree to proceed
                    </Typography>
                  </div>
                )}
              />
              <Button
                type="submit"
                variant="filled"
                className="flex w-full items-center justify-center bg-primary">
                Proceed to External Contribution Page
              </Button>
              <Typography
                variant="body2"
                color="error"
                className={!noContributionUrlError && 'hidden'}>
                The campaigner has not yet set the contribution url in the
                fundraiser. Please ask them to set it up there and comeback.
              </Typography>
            </div>
          </form>

          <Typography
            variant="caption"
            color="textSecondary"
            className="mt-4 pb-14 text-justify lg:pb-0">
            Please be aware that we are redirecting you to third-party websites
            for contribution transactions. Neki is not responsible for the
            contribution transaction or the content or practices of these
            external sites and does not process contributions directly. Your
            transactions and confirmations of contributions are managed
            independently on the third-party site, and you are subject to their
            terms and conditions. After completing your contribution on the
            third-party site, it&apos;s important to confirm the transaction on
            Neki&apos;s platform. This confirmation may require you to provide
            proof of other forms of verification of the contribution. We
            encourage you to review these points for a clear understanding of
            our process and your responsibilities.
          </Typography>
        </div>
      </div>
    </div>
  );
};

const ConfirmContributionScreen = (props) => {
  const {
    user,
    post,
    fundraisingOrg,
    isLoggedIn,
    handleClose,
    contributionIntent,
    setShowSuccess,
    setTransactionState,
  } = props;

  const contributionUrl = post?.sourcePostData?.contributionUrl;

  const { mutate: updateFundraiserContribution } =
    useUpdateFundraiserDonation();
  const isLessThanSmallScreen = useBreakpointHooks((bp) => bp.useSmaller('sm'));

  const [savedAmount, setSavedAmount] = useState(null);
  const [donationImage, setContributionImage] = useState([]);
  const [isAmountEditable, setIsAmountEditable] = useState(false);
  const [donationProofError, setContributionProofError] = useState(false);

  const address = parseAddress(fundraisingOrg?.address);
  const addressString = [
    address.address,
    address.city,
    address.state,
    address.country,
    address.zipcode,
  ]
    .filter((item) => !isEmpty(trim(item)))
    .join(', ');

  const schema = yup.object().shape({
    amount: yup
      .number()
      .min(1, 'Minimum amount is 1')
      .typeError('Minimum amount is 1')
      .nullable()
      .notRequired(),
  });

  const formMethods = useForm({
    mode: 'onChange',
    defaultValues: {
      amount: contributionIntent?.amount || 0,
    },
    resolver: yupResolver(schema),
  });

  const {
    handleSubmit,
    formState: { errors },
    control,
    setValue,
    getValues,
  } = formMethods;

  const handleEditAmount = () => {
    setIsAmountEditable((prevIsAmountEditable) => !prevIsAmountEditable);
  };

  const handleSaveAmount = () => {
    const values = getValues();
    setSavedAmount(values.amount);
    handleEditAmount();
  };

  const handleClearAmount = () => {
    setValue('amount', contributionIntent?.amount);
    handleEditAmount();
  };

  const handleUpdateContributionImage = (image) => {
    setContributionImage(image);
    setContributionProofError(false);
  };

  const handleUpdateFundraiserContribution = (amount) => {
    updateFundraiserContribution({
      postId: post?.id || contributionIntent?.postId,
      amount: amount,
      donor: isLoggedIn()
        ? user
        : {
            id: -1,
            first_name: contributionIntent.firstName,
            last_name: contributionIntent.lastName,
            avatar: null,
          },
    });
  };

  const handleFormSubmit = async (data) => {
    const formData = new FormData();

    if (donationImage.length) {
      setContributionProofError(false);
      formData.append('donationProof', first(donationImage));
    } else {
      setContributionProofError(true);
      return;
    }

    const payload = {
      fundraisingOrgId: fundraisingOrg.id,
      intentId: contributionIntent?._id,
      donorId: contributionIntent?.donorId,
      donationId: contributionIntent?.donationId,
      locationId: contributionIntent?.locationId,
      currency: contributionIntent?.currency,
    };

    if (data.amount && isNumber(data.amount)) {
      payload.amount = data.amount;
    }

    formData.append('data', JSON.stringify(payload));

    if (isLoggedIn()) {
      const { ok, data: res } = await authorizedServerFetch(
        `users/me/external/contributions/confirm`,
        {
          method: 'POST',
          body: formData,
        },
      );

      console.log('ConfirmContributionScreen', res);

      if (ok) {
        const processedAmount = res?.data?.totalContributed || data.amount;
        handleUpdateFundraiserContribution(processedAmount);
        setTransactionState('confirmed');
        setShowSuccess('user-external-contribution');
      } else {
        // toast('Error confirming contribution', { type: 'error' });
      }
    } else {
      /* empty */

      const res = await fetch(
        `${process.env.NEXT_PUBLIC_API_HOST}/v1/public/contributions/external/confirm`,
        {
          method: 'POST',
          body: formData,
        },
      );

      console.log('ConfirmContributionScreen', res);

      if (res.ok) {
        const { data: responseData } = await res.json();
        console.log('ConfirmContributionScreen', responseData);
        handleUpdateFundraiserContribution(data.amount);
        setTransactionState('confirmed');
        setShowSuccess('user-external-contribution');
      } else {
        // toast('Error confirming contribution', { type: 'error' });
      }
    }
  };

  const handleCancelContribution = async () => {
    const formData = new FormData();

    const payload = {
      intentId: contributionIntent?._id,
    };

    formData.append('data', JSON.stringify(payload));

    if (isLoggedIn()) {
      const { ok } = await authorizedServerFetch(
        `users/me/external/contributions/confirm`,
        {
          method: 'PUT',
          body: formData,
        },
      );

      if (!ok) {
        console.log('Error in cancelling contribution');
        return;
      }
    } else {
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_API_HOST}/v1/public/contributions/external/confirm`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: formData,
        },
      );

      console.log('handleCancelContribution', res);

      if (!res.ok) {
        const { data } = await res.json();
        console.log('Error in cancelling contribution');
        return;
      }
    }

    setTransactionState('cancelled');
    handleClose();
  };

  useEffect(() => {
    setValue('amount', contributionIntent?.amount);
  }, [contributionIntent, setValue]);

  if (contributionIntent && contributionIntent.status !== 'pledged') {
    const status =
      contributionIntent?.status === 'confirmed' ? 'Confirmed' : 'Cancelled';
    return (
      <div className="flex flex-col">
        <div className="flex h-full flex-1 overflow-y-auto overflow-x-hidden">
          <div className="flex w-full items-center justify-between gap-4">
            <div className="mt-2 flex items-center justify-start truncate text-26 font-bold">
              Contribution {status}
            </div>
          </div>
        </div>
        <form
          className="mt-2 flex flex-col justify-start gap-4"
          onSubmit={handleSubmit(handleFormSubmit)}>
          <div className="flex flex-col items-start gap-4">
            <Typography variant="subtitle1">
              {contributionIntent?.status === 'confirmed'
                ? `Thank you! Your contribution has already been confirmed. We appreciate your generosity and support.`
                : `Your contribution has been cancelled. Please create another pledge and confirm it if you'd like to proceed.`}
            </Typography>
          </div>

          <div
            className={`flex w-full items-center justify-end gap-4 p-3 ${
              isLessThanSmallScreen ? 'mb-20 flex-col' : 'mb-4'
            }`}>
            <Button
              variant="outlined"
              color="primary"
              className={`${isLessThanSmallScreen ? 'w-full' : ''}`}
              onClick={handleClose}>
              Close
            </Button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <div className="flex h-full flex-1 overflow-y-auto overflow-x-hidden">
        <div className="flex w-full items-center justify-between gap-4">
          <div className="mt-2 flex items-center justify-start truncate text-26 font-bold">
            Confirm Contribution
          </div>
        </div>
      </div>
      <form
        className="mt-2 flex flex-col justify-start gap-4"
        onSubmit={handleSubmit(handleFormSubmit)}>
        <div className="flex flex-col items-start gap-4">
          <Typography variant="subtitle1">
            Have you successfully made your contribution? If not,{' '}
            <a
              className="underline"
              href={contributionUrl}
              target="_blank"
              rel="noopener noreferrer">
              click here{' '}
            </a>
            to contribute
          </Typography>
          <div className="mt-4 grid w-full grid-cols-2 items-center gap-x-1 gap-y-3">
            {/* <Typography variant="subtitle1" className="font-bold">
              Nonprofit name:
            </Typography>
            <Typography variant="body1" className="truncate">
              {nonProfit?.name}
            </Typography>

            <Typography variant="subtitle1" className="font-bold">
              Nonprofit address:
            </Typography>
            <Typography variant="body1" className="truncate">
              {addressString}
            </Typography>

            <Typography variant="subtitle1" className="font-bold">
              Nonprofit taxId:
            </Typography>
            <Typography variant="body1">{nonProfit?.ein}</Typography> */}

            <Typography variant="subtitle1" className="font-bold">
              Contribution amount:
              {isAmountEditable && <span className="ml-1 text-red-700">*</span>}
            </Typography>
            <Typography variant="body1">
              {!isAmountEditable && (
                <div className="flex flex-row items-center gap-4">
                  <Typography>
                    {getSymbolFromCurrency(contributionIntent?.currency)}{' '}
                    {savedAmount || contributionIntent?.amount}
                  </Typography>
                  <Button
                    variant="outlined"
                    className="flex"
                    onClick={handleEditAmount}>
                    <MdEdit className="h-6 w-6" />
                  </Button>
                </div>
              )}
            </Typography>
            {isAmountEditable && (
              <div className="flex flex-row items-start gap-4">
                <Controller
                  name="amount"
                  control={control}
                  render={({ field: { ref, ...rest } }) => (
                    <Input
                      placeholder="Enter an amount"
                      required
                      {...rest}
                      adornment={
                        <Typography
                          variant="body1"
                          color="textSecondary"
                          className="!font-medium">
                          {getSymbolFromCurrency(contributionIntent?.currency)}
                        </Typography>
                      }
                      error={errors.amount?.message}
                    />
                  )}
                />
                <Button
                  variant="outlined"
                  className="flex items-center justify-center"
                  onClick={handleSaveAmount}>
                  <MdCheck className="h-6 w-6" />
                </Button>
                <Button
                  variant="filled"
                  className="flex items-center justify-center"
                  onClick={handleClearAmount}>
                  <MdClose className="white h-6 w-6" />
                </Button>
              </div>
            )}
          </div>
        </div>
        <div className="mt-2">
          <PostImageSelector
            value={donationImage}
            onChange={handleUpdateContributionImage}
            maxFiles={1}
            autoOpen={false}
            dropBoxMessage={
              'Upload your contribution receipt or another proof of contribution'
            }
          />
          <Typography
            variant="caption"
            color="error"
            className={!donationProofError && 'hidden'}>
            Contribution proof is required
          </Typography>
        </div>

        <div
          className={`flex w-full items-center justify-end gap-4 p-3 ${
            isLessThanSmallScreen ? 'mb-20 flex-col' : 'mb-4'
          }`}>
          <Button
            variant="outlined"
            color="primary"
            className={`${isLessThanSmallScreen ? 'w-full' : ''}`}
            onClick={handleCancelContribution}>
            NO, Cancel the contribution
          </Button>
          <Button
            variant="outlined"
            color="primary"
            className={`${isLessThanSmallScreen ? 'w-full' : ''}`}
            onClick={handleClose}>
            Remind me later
          </Button>
          <Button
            variant="filled"
            type="submit"
            className={`${isLessThanSmallScreen ? 'w-full' : ''}`}>
            Confirm
          </Button>
        </div>
      </form>
    </div>
  );
};

function ExternalContributionForm(props) {
  const {
    user,
    post,
    amount,
    fundraisingOrg,
    isLoggedIn,
    handleClose,
    contributionIntent,
    setShowSuccess,
    setLoggedOutUser,
    handleFetchContributionIntent,
  } = props;

  const [transactionState, setTransactionState] = useState(
    contributionIntent ? 'confirm' : 'pending',
  );

  const renderExternalContributionScreen = () => {
    // const status = 'confirm';
    switch (transactionState) {
      case 'pending':
        return (
          <PendingContributionScreen
            user={user}
            post={post}
            amount={amount}
            fundraisingOrg={fundraisingOrg}
            isLoggedIn={isLoggedIn}
            setShowSuccess={setShowSuccess}
            setLoggedOutUser={setLoggedOutUser}
            setTransactionState={setTransactionState}
            handleFetchContributionIntent={handleFetchContributionIntent}
          />
        );
      case 'confirm':
        return (
          <ConfirmContributionScreen
            user={user}
            post={post}
            fundraisingOrg={fundraisingOrg}
            isLoggedIn={isLoggedIn}
            handleClose={handleClose}
            contributionIntent={contributionIntent}
            setShowSuccess={setShowSuccess}
            setTransactionState={setTransactionState}
          />
        );
      default:
        return '';
    }
  };

  return <>{renderExternalContributionScreen()}</>;
}

export default ExternalContributionForm;
